import { motion } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns/formatDistanceToNow';
import { User, AlertCircle } from 'lucide-react';
import type { Conversation } from '@/types/api';
import { cn } from '@/lib/utils';
import { PlatformBadge } from './PlatformIcon';
import { LIST_ROW, UNREAD_PULSE, UNREAD_PULSE_TRANSITION } from '../lib/motion';

const STATUS_DOT: Record<Conversation['status'], string> = {
  active: 'bg-emerald-500',
  pending: 'bg-amber-500',
  resolved: 'bg-slate-400',
  human: 'bg-rose-500',
};

const STATUS_LABEL: Record<Conversation['status'], string> = {
  active: 'Active',
  pending: 'Pending',
  resolved: 'Resolved',
  human: 'Needs Human',
};

export const ConversationItem = ({
  conversation,
  isSelected,
  onClick,
}: {
  conversation: Conversation;
  isSelected: boolean;
  onClick: () => void;
}) => {
  const isHuman = conversation.status === 'human';

  return (
    <motion.button
      type="button"
      variants={LIST_ROW}
      onClick={onClick}
      aria-current={isSelected}
      className={cn(
        'group relative flex w-full items-start gap-2.5 rounded-xl p-2.5 text-left transition-all duration-150 border',
        isSelected
          ? 'bg-slate-900 dark:bg-slate-800 text-white border-slate-900 dark:border-slate-700 shadow-xs'
          : 'bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800/60 border-slate-200/70 dark:border-slate-800 hover:border-slate-300/80 dark:hover:border-slate-700 shadow-2xs'
      )}
    >
      {/* Avatar + platform corner badge */}
      <div className="relative shrink-0">
        <div
          className={cn(
            'flex h-9 w-9 items-center justify-center rounded-full font-semibold text-xs transition-colors',
            isSelected
              ? 'bg-slate-800 dark:bg-slate-700 text-slate-200 border border-slate-700 dark:border-slate-600'
              : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200/80 dark:border-slate-700'
          )}
        >
          {conversation.customerName ? conversation.customerName.charAt(0).toUpperCase() : <User className="h-4 w-4 text-slate-400 dark:text-slate-500" />}
        </div>
        <PlatformBadge platform={conversation.platform} className="absolute -bottom-0.5 -right-0.5 ring-2 ring-white dark:ring-slate-900" />
      </div>

      <div className="min-w-0 flex-1">
        {/* Name + time */}
        <div className="flex items-center justify-between gap-1.5">
          <span className={cn('truncate text-xs font-bold', isSelected ? 'text-white' : 'text-slate-900 dark:text-slate-100')}>
            {conversation.customerName}
          </span>
          <span className={cn('shrink-0 text-[10px] tabular-nums', isSelected ? 'text-slate-400' : 'text-slate-400 dark:text-slate-500')}>
            {formatDistanceToNow(new Date(conversation.timestamp), { addSuffix: false })}
          </span>
        </div>

        {/* Message snippet */}
        <p className={cn('mt-0.5 truncate text-[11px] leading-tight', isSelected ? 'text-slate-300' : 'text-slate-500 dark:text-slate-400')}>
          {conversation.lastMessage || 'No messages yet'}
        </p>

        {/* Status indicator row */}
        <div className="mt-1.5 flex items-center justify-between gap-1">
          <div className="flex items-center gap-1.5">
            <span
              className={cn('h-1.5 w-1.5 shrink-0 rounded-full', STATUS_DOT[conversation.status])}
              title={STATUS_LABEL[conversation.status]}
            />
            <span className={cn('text-[10px] font-medium', isSelected ? 'text-slate-400' : 'text-slate-400 dark:text-slate-500')}>
              {STATUS_LABEL[conversation.status]}
            </span>
            {isHuman && (
              <span className={cn(
                'inline-flex items-center gap-0.5 rounded px-1 py-0.2 text-[9px] font-bold',
                isSelected ? 'bg-rose-500/20 text-rose-300' : 'bg-rose-50 dark:bg-rose-950/50 text-rose-600 dark:text-rose-400 border border-rose-200 dark:border-rose-800/80'
              )}>
                <AlertCircle className="h-2.5 w-2.5" />
                Human
              </span>
            )}
          </div>

          {conversation.unreadCount > 0 && (
            <motion.span
              animate={UNREAD_PULSE}
              transition={UNREAD_PULSE_TRANSITION}
              className={cn(
                'flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[9px] font-bold',
                isSelected
                  ? 'bg-primary text-white shadow-xs'
                  : 'bg-primary text-white shadow-2xs'
              )}
            >
              {conversation.unreadCount}
            </motion.span>
          )}
        </div>
      </div>
    </motion.button>
  );
};
